import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Slider from "react-slick";
import Button from "react-bootstrap/Button";
import Card from "react-bootstrap/Card";
import Spinner from "react-bootstrap/Spinner";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faClock, faChevronLeft, faChevronRight, faMapMarkerAlt } from "@fortawesome/free-solid-svg-icons";
import styled from "styled-components";
import "./OurOffer.css";
import API_BASE_URL from "../config/api";

// ---------------------- Styled Components ----------------------
const Container = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: auto;
  padding: ${(props) => (props.isMobile ? "40px 0" : "80px 0")};
  width: 100%;
  background: linear-gradient(to bottom, #f8fcfd, #ffffff);
`;

const InnerContainer = styled.div`
  max-width: 1440px;
  width: ${(props) => (props.isMobile ? "100%" : "90%")};
  margin: 0 auto;
  text-align: center;
`;

const HeaderBlock = styled.div`
  margin-bottom: ${(props) => (props.isMobile ? "30px" : "60px")};
  padding: 0 20px;
`;

const BestRecommended = styled.p`
  font-family: "MonteCarlo", cursive;
  font-size: ${(props) => (props.isMobile ? "28px" : "48px")};
  color: #1CA8CB;
  margin-bottom: 10px;
`;

const PopularDestinations = styled.h2`
  font-family: "Merriweather", serif;
  font-size: ${(props) => (props.isMobile ? "24px" : "42px")};
  font-weight: 700;
  color: #004e61;
  margin-bottom: 15px;
  line-height: 1.3;
`;

const Description = styled.p`
  font-family: "Merriweather", serif;
  font-size: ${(props) => (props.isMobile ? "14px" : "18px")};
  font-weight: 300;
  color: #666;
  max-width: 700px;
  margin: 0 auto;
  line-height: 1.6;
`;

const SlideWrapper = styled.div`
  margin: ${(props) => (props.isMobile ? "0 8px" : "0 15px")};
  padding: 10px 0 30px 0; /* Space for shadow */
  box-sizing: border-box;
`;

const StyledCard = styled(Card)`
  border: none !important;
  border-radius: 20px !important;
  overflow: hidden;
  box-shadow: 0 10px 30px rgba(0,0,0,0.05);
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  background: white;
  width: 320px !important; /* Fixed width for consistent slides */
  height: 500px !important;

  &:hover {
    transform: translateY(-10px);
    box-shadow: 0 20px 40px rgba(0,0,0,0.12);
  }

  .card-img-top {
    width: 100%;
    height: 280px;
    object-fit: cover;
    transition: transform 0.5s ease;
  }

  &:hover .card-img-top {
    transform: scale(1.05);
  }
`;

const CardBody = styled(Card.Body)`
  padding: 24px !important;
  text-align: left;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  height: 220px; /* Remaining height */
`;

const CardContent = styled.div``;

const CardName = styled.h3`
  font-family: "Merriweather", serif;
  font-size: 20px;
  font-weight: 700;
  color: #333;
  margin-bottom: 8px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const CardLocation = styled.p`
  font-family: "Poppins", sans-serif;
  font-size: 14px;
  color: #888;
  display: flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 15px;
`;

const PriceTag = styled.div`
  font-family: "Merriweather", serif;
  font-size: 18px;
  font-weight: 700;
  color: #004e61;
`;

const CardFooter = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: 15px;
  border-top: 1px solid #f0f0f0;
  padding-top: 15px;
`;

const CardTime = styled.span`
  font-family: "Poppins", sans-serif;
  font-size: 13px;
  color: #666;
  display: flex;
  align-items: center;
  gap: 6px;
`;

const StyledButton = styled(Button)`
  padding: 8px 20px;
  border-radius: 50px;
  background-color: #02495b;
  border: none;
  font-size: 14px;
  font-weight: 500;
  color: white;
  transition: all 0.2s ease;

  &:hover {
    background-color: #1CA8CB;
    transform: translateX(3px);
  }
`;

const ArrowButton = styled.div`
  width: 48px;
  height: 48px;
  background-color: white;
  box-shadow: 0 4px 12px rgba(0,0,0,0.1);
  border-radius: 50%;
  display: flex;
  justify-content: center;
  align-items: center;
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  z-index: 2;
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    background-color: #02495b;
    color: white;
    box-shadow: 0 8px 20px rgba(0,0,0,0.15);
  }

  /* Hide on mobile for cleaner look, swipe is intuitive */
  @media (max-width: 768px) {
    display: none;
  }
`;

// Responsive mobile tweaks in CSS, handled primarily by styled-components props
// overrides for slick-slider are handled in logic below

const PrevArrow = ({ onClick, style }) => (
  <ArrowButton style={{ ...style, left: -60, display: "flex" }} onClick={onClick}>
    <FontAwesomeIcon icon={faChevronLeft} />
  </ArrowButton>
);

const NextArrow = ({ onClick, style }) => (
  <ArrowButton style={{ ...style, right: -60, display: "flex" }} onClick={onClick}>
    <FontAwesomeIcon icon={faChevronRight} />
  </ArrowButton>
);

function OurOffer() {
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const isMobile = windowWidth <= 768;

  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    const fetchPackages = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/packages`);
        const result = await response.json();
        // Filter packages that are active and marked as favorite
        const filteredPackages = result.filter(
          (pkg) => pkg.isActive && pkg.isFavorite
        );
        setPackages(filteredPackages);
      } catch (error) {
        console.error("Error fetching packages:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchPackages();
  }, []);

  const formatDateDuration = (start, end) => {
    const s = new Date(start);
    const e = new Date(end);
    const diffTime = Math.abs(e - s);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return `${diffDays} Days`; // Showing duration usually looks cleaner on cards than full dates
  };

  const settings = {
    dots: true,
    infinite: packages.length > 3, // Only loop if we have enough items
    speed: 600,
    slidesToShow: 3,
    slidesToScroll: 1,
    centerMode: true,
    centerPadding: "0px",
    variableWidth: true, // Allow custom card widths
    prevArrow: <PrevArrow />,
    nextArrow: <NextArrow />,
    responsive: [
      {
        breakpoint: 1200,
        settings: {
          slidesToShow: 2, // Standard
          variableWidth: true,
          centerMode: true,
        },
      },
      {
        breakpoint: 768,
        settings: {
          slidesToShow: 1,
          centerMode: true,
          centerPadding: "30px", // Show peek of next card
          variableWidth: false, // Use full width approach for mobile stability
          dots: false,
        },
      },
      {
        breakpoint: 480,
        settings: {
          slidesToShow: 1,
          centerMode: true, // Crucial for 'peek' effect
          centerPadding: "40px", // Significant padding to show next slide
          variableWidth: false,
          arrows: false,
        }
      }
    ],
  };

  return (
    <Container isMobile={isMobile}>
      <InnerContainer isMobile={isMobile}>
        {loading ? (
          <div style={{ textAlign: "center", padding: "100px 0" }}>
            <Spinner animation="border" role="status" style={{ color: "#02495B" }}>
              <span className="visually-hidden">Loading...</span>
            </Spinner>
          </div>
        ) : (
          <>
            <HeaderBlock isMobile={isMobile}>
              <BestRecommended isMobile={isMobile}>
                Best Recommended Packages
              </BestRecommended>
              <PopularDestinations isMobile={isMobile}>
                Popular Destinations We Offer For All
              </PopularDestinations>
              <Description isMobile={isMobile}>
                Whether you're dreaming of white sandy beaches, vibrant cityscapes,
                or serene mountain getaways, Trippro has something for everyone.
              </Description>
            </HeaderBlock>

            <Slider {...settings} className="custom-slick-slider">
              {packages.map((pkg, index) => (
                <SlideWrapper key={index} isMobile={isMobile}>
                  <StyledCard className="card-important-styles">
                    <Card.Img variant="top" src={pkg.coverImage} />
                    <CardBody>
                      <CardContent>
                        <CardName>{pkg.name}</CardName>
                        <CardLocation>
                          <FontAwesomeIcon icon={faMapMarkerAlt} style={{ color: "#1CA8CB" }} />
                          {pkg.itinerary?.[0]?.destinations?.[0]?.destinationId?.name || "Multiple Locations"}
                        </CardLocation>
                        <PriceTag>₹ {pkg.totalPrice.toLocaleString()}/-</PriceTag>
                      </CardContent>

                      <CardFooter>
                        <CardTime>
                          <FontAwesomeIcon icon={faClock} />
                          {formatDateDuration(pkg.startDate, pkg.endDate)}
                        </CardTime>
                        <StyledButton
                          onClick={() => navigate(`/tripdetails?name=${pkg.slug}`)}
                        >
                          Book Now
                        </StyledButton>
                      </CardFooter>
                    </CardBody>
                  </StyledCard>
                </SlideWrapper>
              ))}
            </Slider>
          </>
        )}
      </InnerContainer>
    </Container>
  );
}

export default OurOffer;